import { Routes } from '@angular/router';

import {
  authGuard,
  internalAdminGuard,
  landingPageGuard,
  joinRequestGuard,
  publicOnlyGuard,
  signupGuard,
} from './auth/auth.guards';
import { orgFeatureGuard, orgFeatureGuardUnlessInternal } from './orgs/org-feature.guard';
import { orgPermissionGuard } from './orgs/org-permission.guard';
import { pendingChangesGuard } from './shared/pending-changes.guard';
import { leaveCaptureGuard } from './recordings/recording-capture/leave-capture.guard';

/**
 * Top-level route table.
 *
 * Authenticated routes live as children of the shell route so the
 * sidebar/topbar stay mounted across navigation. Each leaf carries
 * `data` consumed by the topbar (title, breadcrumb section).
 *
 * The route paths are intentionally flat (`/demonstrations`, `/nodes`, …)
 * even when the sidebar groups them — see nav.config.ts. Regrouping in
 * the sidebar later won't break deep links or bookmarks.
 */
export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [landingPageGuard],
    loadComponent: () => import('./landing/landing.component').then((m) => m.LandingComponent),
    title: 'GHOSTS · AI UI Test Automation That Watches the Screen',
  },
  {
    path: 'pricing',
    canActivate: [landingPageGuard],
    loadComponent: () =>
      import('./landing/pricing/pricing.component').then((m) => m.PricingComponent),
    title: 'Pricing · GHOSTS',
  },
  {
    path: 'docs',
    canActivate: [landingPageGuard],
    loadComponent: () => import('./landing/docs/docs.component').then((m) => m.DocsComponent),
    title: 'Docs · GHOSTS',
  },
  {
    // NO landingPageGuard: the Imprint is a legal requirement in Austria and
    // has to be reachable by anybody who follows the footer link, signed in or
    // not. The marketing pages bounce a signed-in visitor to their dashboard;
    // a legal notice that does that is a legal notice you cannot read.
    path: 'imprint',
    loadComponent: () =>
      import('./landing/legal/imprint.component').then((m) => m.ImprintComponent),
    title: 'Imprint · GHOSTS',
  },
  {
    path: 'login',
    canActivate: [publicOnlyGuard],
    loadComponent: () => import('./auth/login.component').then((m) => m.LoginComponent),
    title: 'Sign in · GHOSTS',
  },
  {
    path: 'signup',
    canActivate: [signupGuard],
    loadComponent: () => import('./auth/signup.component').then((m) => m.SignupComponent),
    title: 'Create account · GHOSTS',
  },
  {
    path: 'join-request',
    canActivate: [joinRequestGuard],
    loadComponent: () =>
      import('./auth/join-request.component').then((m) => m.JoinRequestComponent),
    title: 'Join request · GHOSTS',
  },
  {
    path: 'forgot-password',
    canActivate: [publicOnlyGuard],
    loadComponent: () =>
      import('./auth/forgot-password.component').then((m) => m.ForgotPasswordComponent),
    title: 'Reset password · GHOSTS',
  },
  {
    // Where the links in account emails land (reset, invitation, ...). NO
    // guard: a link must work whoever is signed in, and the page asks before
    // it switches accounts.
    path: 'auth/action',
    loadComponent: () =>
      import('./auth/action/auth-action.component').then((m) => m.AuthActionComponent),
    title: 'Account · GHOSTS',
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./shell/shell.component').then((m) => m.ShellComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        // The post-login landing target (see auth.guards.ts) — no guard;
        // the page gates per-widget instead of bouncing users elsewhere.
        path: 'dashboard',
        loadComponent: () =>
          import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
        data: { title: 'Dashboard' },
        title: 'Dashboard · GHOSTS',
      },
      {
        // Customer-facing vocabulary: "demonstrations". Internal names
        // (folders, services, Firestore collection, permissions) keep the
        // original "recordings" — renaming those is churn/data-migration
        // for zero customer value.
        path: 'demonstrations',
        canActivate: [orgPermissionGuard('recordings:read')],
        loadComponent: () =>
          import('./recordings/recordings-list/recordings-list.component').then(
            (m) => m.RecordingsListComponent,
          ),
        data: { title: 'Demonstrations' },
        title: 'Demonstrations · GHOSTS',
      },
      {
        // Setup became a dialog in #244, so this URL no longer renders
        // anything of its own. Kept as a redirect because it is linked from
        // older screens, bookmarks and the docs, and the list is where the
        // button that opens the dialog now lives.
        path: 'demonstrations/new',
        pathMatch: 'full',
        redirectTo: 'demonstrations',
      },
      {
        path: 'demonstrations/capture',
        canActivate: [orgPermissionGuard('recordings:write')],
        canDeactivate: [leaveCaptureGuard],
        loadComponent: () =>
          import('./recordings/recording-capture/recording-capture.component').then(
            (m) => m.RecordingCaptureComponent,
          ),
        data: {
          title: 'Recording',
          // Flush because the framebuffer is the screen (#244): the browser
          // will not grant fullscreen without a gesture, so the windowed
          // layout has to give the VM everything the shell is not using.
          layout: 'flush',
          trail: [{ label: 'Demonstrations', link: '/demonstrations' }],
        },
        title: 'Recording · GHOSTS',
      },
      {
        // Back into a demonstration still in progress (P79): after navigating
        // away, a reload, or a dropped connection. The same screen; it loads
        // the recording instead of starting one. Before `demonstrations/:id`.
        path: 'demonstrations/:id/capture',
        canActivate: [orgPermissionGuard('recordings:write')],
        canDeactivate: [leaveCaptureGuard],
        loadComponent: () =>
          import('./recordings/recording-capture/recording-capture.component').then(
            (m) => m.RecordingCaptureComponent,
          ),
        data: {
          title: 'Recording',
          layout: 'flush',
          trail: [{ label: 'Demonstrations', link: '/demonstrations' }],
        },
        title: 'Recording · GHOSTS',
      },
      {
        path: 'demonstrations/:id',
        canActivate: [orgPermissionGuard('recordings:read')],
        loadComponent: () =>
          import('./recordings/recording-detail/recording-detail.component').then(
            (m) => m.RecordingDetailComponent,
          ),
        data: {
          title: 'Demonstration',
          trail: [{ label: 'Demonstrations', link: '/demonstrations' }],
        },
        title: 'Demonstration · GHOSTS',
      },
      {
        path: 'secrets',
        canActivate: [orgPermissionGuard('secrets:read')],
        loadComponent: () =>
          import('./secrets/secrets-list/secrets-list.component').then(
            (m) => m.SecretsListComponent,
          ),
        data: { title: 'Vault' },
        title: 'Vault · GHOSTS',
      },
      {
        path: 'nodes',
        canActivate: [orgPermissionGuard('nodes:read')],
        loadComponent: () =>
          import('./nodes/nodes-list/nodes-list.component').then((m) => m.NodesListComponent),
        // `flush`: the library is three panes that scroll independently, so
        // the shell drops its centred column and its page scroll (P46).
        data: { title: 'Node library', layout: 'flush' },
        title: 'Node library · GHOSTS',
      },
      {
        path: 'nodes/review',
        canActivate: [
          orgPermissionGuard('nodes:read'),
          orgFeatureGuardUnlessInternal('nodeEditing'),
        ],
        loadComponent: () =>
          import('./nodes/review/change-set-list/change-set-list.component').then(
            (m) => m.ChangeSetListComponent,
          ),
        data: { title: 'Node review', trail: [{ label: 'Node library', link: '/nodes' }] },
        title: 'Node review · GHOSTS',
      },
      {
        path: 'nodes/review/:changeSetId',
        canActivate: [
          orgPermissionGuard('nodes:read'),
          orgFeatureGuardUnlessInternal('nodeEditing'),
        ],
        loadComponent: () =>
          import('./nodes/review/change-set-detail/change-set-detail.component').then(
            (m) => m.ChangeSetDetailComponent,
          ),
        data: {
          title: 'Change set',
          trail: [
            { label: 'Node library', link: '/nodes' },
            { label: 'Node review', link: '/nodes/review' },
          ],
        },
        title: 'Change set · GHOSTS',
      },
      {
        // The review inspector: the node editor, read-only, on a PROPOSAL
        // rather than a library node. `nodes:read` rather than
        // `nodes:write` -- looking at a proposal is not editing one.
        path: 'nodes/review/:changeSetId/:changeId',
        canActivate: [
          orgPermissionGuard('nodes:read'),
          orgFeatureGuardUnlessInternal('nodeEditing'),
        ],
        loadComponent: () =>
          import('./nodes/workbench/node-workbench.component').then(
            (m) => m.NodeWorkbenchComponent,
          ),
        // `flush`: the workbench is three panes that scroll independently
        // (as the library is), so the shell drops its centred column.
        data: {
          title: 'Proposed node',
          layout: 'flush',
          trail: [
            { label: 'Node library', link: '/nodes' },
            { label: 'Node review', link: '/nodes/review' },
            { label: 'Change set', link: '/nodes/review/:changeSetId' },
          ],
        },
        title: 'Proposed node · GHOSTS',
      },
      {
        // `new` is a nodeId here: /nodes/new/edit starts a blank node.
        path: 'nodes/:nodeId/edit',
        canActivate: [orgPermissionGuard('nodes:write'), orgFeatureGuard('nodeEditing')],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./nodes/workbench/node-workbench.component').then(
            (m) => m.NodeWorkbenchComponent,
          ),
        data: {
          title: 'Node editor',
          layout: 'flush',
          trail: [{ label: 'Node library', link: '/nodes' }],
        },
        title: 'Node editor · GHOSTS',
      },
      {
        path: 'testcases',
        canActivate: [orgPermissionGuard('testcases:read')],
        loadComponent: () =>
          import('./testcases/testcases-list/testcases-list.component').then(
            (m) => m.TestcasesListComponent,
          ),
        data: { title: 'Tests' },
        title: 'Tests · GHOSTS',
      },
      {
        path: 'testcases/:testcaseId/edit',
        // `testcases:read`, not `:write`: this is a test's only page, so a
        // viewer reads the test here. Without `testcases:write` the editor
        // opens read-only (no save, run, delete or edit controls); the
        // callables refuse a viewer's write either way.
        canActivate: [orgPermissionGuard('testcases:read')],
        canDeactivate: [pendingChangesGuard],
        loadComponent: () =>
          import('./testcases/testcase-editor/testcase-editor.component').then(
            (m) => m.TestcaseEditorComponent,
          ),
        // `flush`: four panes that scroll independently, same as the node
        // library. Without it the editor renders in the centred column with
        // no definite height and the panes collapse.
        data: {
          title: 'Testcase editor',
          trail: [{ label: 'Tests', link: '/testcases' }],
          layout: 'flush',
        },
        title: 'Testcase editor · GHOSTS',
      },
      {
        // "Reports" is the customer-facing home of run history — the page
        // previously lived at /executions before the flat-nav redesign.
        path: 'reports',
        canActivate: [orgPermissionGuard('runs:read')],
        loadComponent: () =>
          import('./runs/runs-list/runs-list.component').then((m) => m.RunsListComponent),
        data: { title: 'Reports' },
        title: 'Reports · GHOSTS',
      },
      {
        path: 'reports/:runId',
        canActivate: [orgPermissionGuard('runs:read')],
        loadComponent: () =>
          import('./runs/run-detail/run-detail.component').then((m) => m.RunDetailComponent),
        // `flush`: the report is two panes that scroll independently, and the
        // left one is a photograph of a whole desktop — inside the centred
        // page column it rendered at about a third of life size (#251).
        data: {
          title: 'Run',
          trail: [{ label: 'Reports', link: '/reports' }],
          layout: 'flush',
        },
        title: 'Run · GHOSTS',
      },
      ...stub('ghosts', 'Ghosts', 'Manage'),
      ...stub('profiles', 'Profiles', 'Manage'),
      {
        // Devices: the kinds of device, the org's profiles for them, and the
        // ones running now. One guard on the shell covers every tab — all of
        // them are readable with `vms:read`; what a tab lets you change is
        // gated by the tab itself and, finally, by the callable.
        path: 'devices',
        canActivate: [orgPermissionGuard('vms:read')],
        loadComponent: () =>
          import('./devices/devices-shell.component').then((m) => m.DevicesShellComponent),
        data: { title: 'Devices' },
        title: 'Devices · GHOSTS',
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./devices/devices-overview.component').then(
                (m) => m.DevicesOverviewComponent,
              ),
            data: { title: 'Overview', section: 'Devices' },
          },
          {
            path: 'profiles',
            loadComponent: () =>
              import('./orgs/settings/vm-profiles.component').then(
                (m) => m.SettingsVmProfilesComponent,
              ),
            data: { title: 'Profiles', section: 'Devices' },
          },
          {
            // One profile: its settings, and the demonstrations and runs that
            // used it. Named after the profile (`bindPageTitle`); the crumb
            // trail is the way back, so the tab strip needs no entry for it.
            path: 'profiles/:profileId',
            loadComponent: () =>
              import('./devices/device-profile-detail.component').then(
                (m) => m.DeviceProfileDetailComponent,
              ),
            data: {
              title: 'Device profile',
              trail: [
                { label: 'Devices', link: '/devices' },
                { label: 'Profiles', link: '/devices/profiles' },
              ],
            },
          },
          {
            path: 'running',
            loadComponent: () =>
              import('./vms/pool/vm-pool.component').then((m) => m.VmPoolComponent),
            data: { title: 'Running', section: 'Devices' },
          },
        ],
      },
      // The VM pool moved under Devices; the old URL is linked from bookmarks
      // and older docs.
      { path: 'vms', pathMatch: 'full', redirectTo: 'devices/running' },
      {
        path: 'settings',
        loadComponent: () =>
          import('./orgs/settings/settings-shell.component').then((m) => m.SettingsShellComponent),
        data: { title: 'Settings' },
        title: 'Settings · GHOSTS',
        children: [
          // No static `'' -> general` redirect: `general` requires
          // `org:manage` (admin/owner only), which would bounce members
          // and viewers to `/`. SettingsShellComponent navigates to the
          // first allowed tab once permissions resolve.
          {
            path: 'general',
            canActivate: [orgPermissionGuard('org:manage')],
            loadComponent: () =>
              import('./orgs/settings/general.component').then((m) => m.SettingsGeneralComponent),
            data: { title: 'General', section: 'Settings' },
          },
          {
            path: 'members',
            canActivate: [orgPermissionGuard('members:read')],
            loadComponent: () =>
              import('./orgs/settings/members.component').then((m) => m.SettingsMembersComponent),
            data: { title: 'Members', section: 'Settings' },
          },
          {
            path: 'departments',
            canActivate: [orgPermissionGuard('departments:read')],
            loadComponent: () =>
              import('./orgs/settings/departments.component').then(
                (m) => m.SettingsDepartmentsComponent,
              ),
            data: { title: 'Departments', section: 'Settings' },
          },
          {
            path: 'api-keys',
            canActivate: [orgPermissionGuard('apiKeys:read')],
            loadComponent: () =>
              import('./orgs/settings/api-keys.component').then((m) => m.SettingsApiKeysComponent),
            data: { title: 'API keys', section: 'Settings' },
          },
          // Device profiles moved to Devices → Profiles; the old tab URL is
          // linked from bookmarks and older docs. The destination carries the
          // `vms:read` guard.
          { path: 'vm-profiles', pathMatch: 'full', redirectTo: '/devices/profiles' },
          {
            path: 'audit-log',
            canActivate: [orgPermissionGuard('auditLog:read')],
            loadComponent: () =>
              import('./orgs/settings/audit-log.component').then(
                (m) => m.SettingsAuditLogComponent,
              ),
            data: { title: 'Audit log', section: 'Settings' },
          },
          {
            path: 'billing',
            canActivate: [orgPermissionGuard('billing:read')],
            loadComponent: () =>
              import('./orgs/settings/billing.component').then((m) => m.SettingsBillingComponent),
            data: { title: 'Billing', section: 'Settings' },
          },
          {
            path: 'billing/change',
            canActivate: [orgPermissionGuard('billing:portal')],
            loadComponent: () =>
              import('./orgs/settings/plan-picker.component').then((m) => m.PlanPickerComponent),
            data: { title: 'Change plan', section: 'Settings' },
          },
        ],
      },
      {
        path: 'account',
        loadComponent: () =>
          import('./account/account-shell.component').then((m) => m.AccountShellComponent),
        data: { title: 'Account' },
        title: 'Account · GHOSTS',
        children: [
          {
            path: 'profile',
            loadComponent: () =>
              import('./account/profile.component').then((m) => m.ProfilePageComponent),
            data: { title: 'Profile', section: 'Account' },
            title: 'Profile · GHOSTS',
          },
          {
            path: 'preferences',
            loadComponent: () =>
              import('./account/preferences.component').then((m) => m.PreferencesPageComponent),
            data: { title: 'Preferences', section: 'Account' },
            title: 'Preferences · GHOSTS',
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./account/security.component').then((m) => m.SecurityPageComponent),
            data: { title: 'Security', section: 'Account' },
            title: 'Security · GHOSTS',
          },
        ],
      },
      {
        path: 'internal/stripe-config',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./billing/internal-stripe-config.component').then(
            (m) => m.InternalStripeConfigComponent,
          ),
        data: { title: 'Stripe config', section: 'Internal' },
        title: 'Stripe config · GHOSTS',
      },
      {
        path: 'internal/orgs',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./orgs/internal-orgs.component').then((m) => m.InternalOrgsComponent),
        data: { title: 'Organizations', section: 'Internal' },
        title: 'Organizations · GHOSTS',
      },
      {
        path: 'internal/admins',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./auth/internal-admins.component').then((m) => m.InternalAdminsComponent),
        data: { title: 'Internal admins', section: 'Internal' },
        title: 'Internal admins · GHOSTS',
      },
      {
        path: 'internal/users',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./auth/internal-users.component').then((m) => m.InternalUsersComponent),
        data: { title: 'Users', section: 'Internal' },
        title: 'Users · GHOSTS',
      },
      {
        path: 'internal/signup-domains',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./auth/internal-signup-domains.component').then(
            (m) => m.InternalSignupDomainsComponent,
          ),
        data: { title: 'Sign-up domains', section: 'Internal' },
        title: 'Sign-up domains · GHOSTS',
      },
      {
        path: 'internal/orgs/:orgId/usage',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./orgs/internal-org-usage.component').then((m) => m.InternalOrgUsageComponent),
        data: {
          title: 'Org usage',
          // The crumb the page's own back link used to be (#240).
          trail: [{ label: 'Internal' }, { label: 'Organizations', link: '/internal/orgs' }],
        },
        title: 'Org usage · GHOSTS',
      },
      {
        path: 'internal/vm-base-images',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./vms/internal-vm-base-images.component').then(
            (m) => m.InternalVmBaseImagesComponent,
          ),
        data: { title: 'VM base images', section: 'Internal' },
        title: 'VM base images · GHOSTS',
      },
      {
        path: 'internal/bundles',
        canActivate: [internalAdminGuard],
        loadComponent: () =>
          import('./runs/internal-bundles.component').then((m) => m.InternalBundlesComponent),
        data: { title: 'Bundles', section: 'Internal' },
        title: 'Bundles · GHOSTS',
      },
      // Catch-all inside the shell — surfaces a real 404 with the chrome
      // intact rather than dumping the user back on the dashboard. We
      // deliberately keep the URL the user typed so they can correct it.
      {
        path: '**',
        loadComponent: () =>
          import('./coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
        data: {
          title: 'Not found',
          section: '404',
          description: "We couldn't find that page. Use the sidebar to get back on track.",
        },
        title: 'Not found · GHOSTS',
      },
    ],
  },
];

/**
 * Helper that fans out a single nav entry into its route definition.
 * Centralised so adding a new stub stays a one-liner.
 */
function stub(path: string, title: string, section?: string): Routes {
  return [
    {
      path,
      loadComponent: () =>
        import('./coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      data: { title, section },
      title: `${title} · GHOSTS`,
    },
  ];
}
